/**
 * What can be seen from where, which is not the same as what can be walked.
 *
 * An arrow goes over a fence and a spell goes through the bars of a cell, and
 * neither of those is somewhere a person can walk. A house wall stops all three.
 * So sight is its own map rather than a reading of the collision map, and what
 * separates the two is a property of the thing standing there: whether it is low
 * enough to see over.
 *
 * Shared, because the client has to know where a fight is walked to. The server
 * decides whether a shot is allowed; the client only predicts where the walk to
 * take it stops, and a prediction worked out by different rules is a character
 * walking up to melee range and being dragged back out to seven tiles.
 */

import type { CollisionMap } from './collision';
import { findPath, tileBeside, withinRange } from './pathfind';
import type { RegionDefinition } from './region';
import type { TilePos, WorldPos } from './tiles';

export class SightMap {
  private readonly blocked: Uint8Array;

  constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly originX: number,
    private readonly originZ: number,
  ) {
    this.blocked = new Uint8Array(width * height);
  }

  private at(tx: number, tz: number): number {
    return (tz - this.originZ) * this.width + (tx - this.originX);
  }

  private inside(tx: number, tz: number): boolean {
    const localX = tx - this.originX;
    const localZ = tz - this.originZ;
    return localX >= 0 && localZ >= 0 && localX < this.width && localZ < this.height;
  }

  set(tx: number, tz: number, blocks: boolean): void {
    if (!this.inside(tx, tz)) return;
    this.blocked[this.at(tx, tz)] = blocks ? 1 : 0;
  }

  blocksSight(tx: number, tz: number): boolean {
    // Off the map is not something to shoot across.
    if (!this.inside(tx, tz)) return true;
    return this.blocked[this.at(tx, tz)] === 1;
  }

  /**
   * Whether one tile can see another.
   *
   * The tiles either end are never counted: something standing in a doorway can
   * be shot at, and something standing behind one cannot. What is checked is the
   * ground between them.
   *
   * Walked tile by tile rather than sampled along the line, because a sampled
   * line skips the tiles it only clips and a shot then passes through the corner
   * of a building. Where a step goes diagonally, both of the tiles it squeezes
   * between have to be clear: two walls meeting at a corner make a corner, not a
   * gap with a wall either side of it.
   */
  canSee(from: { tx: number; tz: number }, to: { tx: number; tz: number }): boolean {
    let tx = from.tx;
    let tz = from.tz;

    const awayX = Math.abs(to.tx - tx);
    const awayZ = Math.abs(to.tz - tz);
    const stepX = to.tx > tx ? 1 : -1;
    const stepZ = to.tz > tz ? 1 : -1;

    // The usual error term, doubled so it stays whole.
    let error = awayX - awayZ;

    // A line can only cross so many tiles; the bound is here so a bad argument
    // cannot spin rather than because it is ever reached.
    for (let step = 0; step < awayX + awayZ + 2; step++) {
      if (tx === to.tx && tz === to.tz) return true;

      const doubled = error * 2;
      const goingX = doubled > -awayZ;
      const goingZ = doubled < awayX;

      if (goingX) error -= awayZ;
      if (goingZ) error += awayX;

      if (goingX && goingZ) {
        // A diagonal step. Blocked on both sides is a corner, not a doorway.
        if (this.blocksSight(tx + stepX, tz) && this.blocksSight(tx, tz + stepZ)) return false;
      }

      if (goingX) tx += stepX;
      if (goingZ) tz += stepZ;

      if (tx === to.tx && tz === to.tz) return true;
      if (this.blocksSight(tx, tz)) return false;
    }

    return true;
  }
}

/**
 * Builds the map for a region.
 *
 * Only the things that say they stop a shot, which is walls and little else.
 * Ground never blocks sight: a hill between two people is a thing this world
 * does not model, and pretending to would mean archers unable to shoot down a
 * slope.
 */
export function buildSightMap(
  region: RegionDefinition,
  stopsShots: (id: number) => boolean,
): SightMap {
  const map = new SightMap(region.width, region.height, region.originX, region.originZ);

  for (const object of region.objects ?? []) {
    if ((object.plane ?? 0) !== 0) continue;
    if (!stopsShots(object.id)) continue;
    map.set(region.originX + object.tx, region.originZ + object.tz, true);
  }

  return map;
}

/** Whether a shot at something can be taken from a tile: near enough, standable, and seen. */
export function canShootFrom(
  map: CollisionMap,
  sight: SightMap,
  tile: TilePos,
  goal: TilePos,
  reach: number,
): boolean {
  if (!withinRange(tile, goal, reach)) return false;
  if (map.isBlocked(tile.tx, tile.tz, tile.plane)) return false;
  return sight.canSee(tile, goal);
}

/**
 * A route cut short at the first place along it a shot can be taken from.
 *
 * Walking at something to hit it from a distance stops as soon as it can be hit,
 * not when the walker arrives beside it.
 */
export function trimToShot(
  points: WorldPos[],
  sight: SightMap,
  goal: TilePos,
  reach: number,
): WorldPos[] {
  if (reach <= 1) return points;

  for (let at = 0; at < points.length; at++) {
    const step = points[at];
    if (!step) continue;

    const tile = { tx: Math.floor(step.x), tz: Math.floor(step.z), plane: step.plane };
    if (!withinRange(tile, goal, reach)) continue;
    if (!sight.canSee(tile, goal)) continue;

    return points.slice(0, at + 1);
  }

  return points;
}

/**
 * The walk to somewhere a target can be shot from.
 *
 * Empty when the tile already stood on will do, so somebody with a shot does
 * not shuffle about taking it. Otherwise the nearest place with a clear line,
 * searched outward from the shooter so the answer is the nearest place to move
 * to rather than the nearest place to the thing being shot at. When nowhere
 * near will do, a walk at the target cut short where the shot comes on, which is
 * how a clear line is usually found. Nothing at all when even that cannot be
 * walked.
 *
 * Asking the pathfinder whether each candidate can be reached is what costs, so
 * it is asked only of places that are otherwise right, and only a few times: an
 * archer cut off from every clear line by a river would otherwise search half a
 * chunk every tick.
 */
export function routeToShoot(
  map: CollisionMap,
  sight: SightMap,
  from: TilePos,
  goal: TilePos,
  reach: number,
): WorldPos[] | null {
  if (canShootFrom(map, sight, from, goal, reach)) return [];

  // Nothing is found while the target is still far off, because a place to shoot
  // from is within reach of it and there is none of that nearby.
  const far = reach + 6;
  let tried = 0;

  search: for (let ring = 1; ring <= far; ring++) {
    for (let dz = -ring; dz <= ring; dz++) {
      for (let dx = -ring; dx <= ring; dx++) {
        // The ring itself, not the square inside it, which has been looked at.
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;

        const spot = { tx: from.tx + dx, tz: from.tz + dz, plane: from.plane };
        if (!canShootFrom(map, sight, spot, goal, reach)) continue;
        if (++tried > 6) break search;

        const route = findPath(map, from, spot);
        const last = route.points[route.points.length - 1];
        if (!last || Math.floor(last.x) !== spot.tx || Math.floor(last.z) !== spot.tz) continue;

        return route.points;
      }
    }
  }

  const beside = tileBeside(map, goal, from);
  if (!beside) return null;

  const route = findPath(map, from, beside);
  if (route.points.length === 0) return null;

  return trimToShot(route.points, sight, goal, reach);
}
